<template>
    <div class="bg-nomad-900 rounded-xl p-6 min-w-0 flex flex-col gap-4">
        <h1 class="text-lg font-semibold text-white">YouTube imports</h1>
        <div class=" text-sm">
            <label for="channel" class="block text-sm font-medium text-nomad-100">Select channel
                <select @change="_getContent" v-model="channelId" class="w-full rounded-lg bg-nomad-800 border border-nomad-700 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent block mt-2">
                    <option :value="channel.id" v-for="channel in channels" v-bind:key="channel.id">{{ channel.name }}
                    </option>
                </select></label>
        </div>
        <div>
            <ul v-if="videos && videos.items && videos.items.length" class="flex flex-col gap-2">
                <li v-for="video in videos.items" class="flex bg-nomad-800 rounded-lg">
                    <div class="flex w-full items-center p-3 gap-3">
                        <div>
                            <img :src="video.snippet.thumbnails.default.url" alt="" class="w-24 h-16 rounded-md object-cover">
                        </div>
                        <div class="flex flex-1 min-w-0 flex-col">
                            <span class="text-nomad-accent text-xs ">{{ video.id.videoId }}</span>
                            <a :href="_youtubeUrl(video.id.videoId)" target="_blank" rel="noopener noreferrer"
                                class="text-white text-sm truncate hover:text-nomad-accent">{{ video.snippet.title }} <span class="icon-external-link" aria-hidden="true"></span></a>
                        </div>
                        <div>
                            <button class="text-nomad-accent" @click="_initVideo(video)"><span
                                    class="icon-download"></span></button>
                        </div>
                    </div>
                </li>
            </ul>
        </div>
        <div class="flex gap-3" v-if="videos && videos.items">
            <button
                class="rounded-lg bg-nomad-800 text-white font-medium px-4 h-11 hover:bg-nomad-700"
                @click="_prev" v-if="videos.prevPageToken">prev</button>
            <button
                class="rounded-lg bg-nomad-800 text-white font-medium px-4 h-11 hover:bg-nomad-700"
                @click="_next" v-if="videos.nextPageToken">next</button>
        </div>
        <div class="fixed inset-0 z-40 flex items-center justify-center bg-nomad-950/80 backdrop-blur p-4" v-if="video">
            <form @submit.prevent="_saveImport" class="flex max-h-full w-full max-w-xl flex-col rounded-xl bg-nomad-900 text-left">
                <div class="flex items-start gap-3 border-b border-nomad-800 p-5">
                    <div class="min-w-0 flex-1">
                        <h2 class="text-lg font-semibold text-white">Add song</h2>
                        <p class="text-xs text-nomad-200">Published {{ video.snippet.publishedAt }}</p>
                    </div>
                    <button type="button" class="icon-x shrink-0 rounded-lg bg-nomad-800 w-9 h-9 text-white hover:bg-nomad-700" aria-label="Close" @click="_dismissEdit"></button>
                </div>
                <div class="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
                    <p class="line-clamp-3 text-sm text-nomad-100" v-if="video.snippet.description">{{ video.snippet.description }}</p>
                    <div>
                        <label for="name" class="block text-sm font-medium text-nomad-100 mb-2">Name</label>
                        <input type="text" id="name" class="w-full rounded-lg bg-nomad-800 border border-nomad-700 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent" v-model="form.name" />
                    </div>
                    <div>
                        <div class="mb-2 flex items-center justify-between">
                            <label for="video" class="text-sm font-medium text-nomad-100">Video ID</label>
                            <a v-if="form.youtube" :href="_youtubeUrl(form.youtube)" target="_blank" rel="noopener noreferrer"
                                class="text-sm font-medium text-nomad-accent hover:underline">Watch on YouTube <span class="icon-external-link" aria-hidden="true"></span></a>
                        </div>
                        <input type="text" id="video" class="w-full rounded-lg bg-nomad-800 border border-nomad-700 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent" v-model="form.youtube" />
                        <div v-if="existingSongs && existingSongs.length" class="mt-2 rounded-lg border border-nomad-accent p-3">
                            <p class="text-sm font-semibold text-nomad-accent">Already on Heesify</p>
                            <a v-for="song in existingSongs" :key="song.id" :href="'/song/' + song.id" target="_blank" rel="noopener noreferrer"
                                class="mt-1 block truncate text-sm text-white hover:text-nomad-accent">{{ song.name }}<span class="text-nomad-200" v-if="song.artist"> by {{ song.artist }}</span> <span class="icon-external-link" aria-hidden="true"></span></a>
                        </div>
                        <p v-else-if="existingSongs" class="mt-2 text-sm text-nomad-200">Not on Heesify yet.</p>
                    </div>
                    <div>
                        <div class="mb-2 flex items-center justify-between">
                            <span class="text-sm font-medium text-nomad-100">Cover</span>
                            <button type="button" class="text-sm text-nomad-accent" v-if="form.cover" @click="_clearCover"><span class="icon-x"></span> Clear</button>
                        </div>
                        <ul class="flex flex-wrap gap-3">
                            <li v-for="thumbnail in video.snippet.thumbnails" class="w-28">
                                <button type="button" class="w-full" @click="_updateCover(thumbnail.url)"><img :src="thumbnail.url" alt=""
                                        class="w-full rounded-lg border-2 border-transparent"
                                        :class="{ '!border-nomad-accent': thumbnail.url == form.cover }" /><span
                                        class="text-nomad-200 text-xs">{{ thumbnail.width }} x {{ thumbnail.height
                                        }}</span></button>
                            </li>
                        </ul>
                    </div>
                    <div>
                        <span class="block text-sm font-medium text-nomad-100 mb-2">Artist <span class="text-red-400">*</span></span>
                        <Selector type="artists" v-model="form.artist" placeholder="Search artist" />
                    </div>
                    <div>
                        <span class="block text-sm font-medium text-nomad-100 mb-2">Featured</span>
                        <Selector type="artists" v-model="form.features" placeholder="Search artist" max="4" />
                    </div>
                    <div>
                        <span class="block text-sm font-medium text-nomad-100 mb-2">Playlists</span>
                        <Selector type="playlists" v-model="form.playlists" placeholder="Search playlist" max="10" />
                    </div>
                    <div>
                        <label for="released" class="block text-sm font-medium text-nomad-100 mb-2">Released</label>
                        <input type="number" id="released" v-model="form.released" class="w-full rounded-lg bg-nomad-800 border border-nomad-700 text-white placeholder:text-nomad-400 focus:border-nomad-accent focus:ring-nomad-accent" placeholder="Year, e.g. 2026" />
                    </div>
                    <div class="flex flex-wrap gap-x-6 gap-y-2">
                        <label for="is_audio_download" class="flex items-center gap-2 text-sm text-white"><input type="checkbox"
                                class="rounded bg-nomad-800 border-nomad-600 text-nomad-accent focus:ring-nomad-accent" id="is_audio_download" v-model="form.meta.is_youtube"
                                value="1" /> <span>Download audio</span></label>
                        <label for="is_smart_music" class="flex items-center gap-2 text-sm text-white"><input type="checkbox" class="rounded bg-nomad-800 border-nomad-600 text-nomad-accent focus:ring-nomad-accent"
                                id="is_smart_music" v-model="form.meta.is_smart" value="1" /> <span>Smart music</span></label>
                    </div>
                </div>
                <div class="flex items-center gap-3 border-t border-nomad-800 p-5">
                    <button type="submit" :disabled="!_canSave"
                        class="shrink-0 rounded-lg bg-nomad-accent text-nomad-900 font-semibold px-6 h-11 hover:bg-nomad-accent/80 disabled:cursor-not-allowed disabled:opacity-40">
                        {{ saving ? 'Saving...' : 'Save' }}
                    </button>
                    <p class="min-w-0 text-sm text-red-400" v-if="error">{{ error }}</p>
                    <p class="min-w-0 text-sm text-nomad-200" v-else-if="!hasArtist">Choose an artist to save.</p>
                </div>
            </form>
        </div>
    </div>
</template>
<script>
import axios from 'axios'
import Selector from "@/Pages/Backstage/Components/Selector.vue";
import { debounce } from '@/utils/helpers';
import { mapActions } from "vuex";
export default {
    data() {
        return {
            video: null,
            saving: false,
            error: null,
            existingSongs: null,
            videos: {},
            page: null,
            channelId: null,
            form: {
                name: null,
                artist: null,
                original_artist: null,
                features: null,
                playlists: null,
                released: null,
                youtube: null,
                cover: null,
                meta: {
                    thumbnail: null,
                    is_youtube: true,
                    cover: false,
                    retry: false,
                    is_smart: false
                },
            },
            channels: [
                {
                    id: "UCfJ23rBnyQok1NV2e0MIEVg",
                    name: "Iftin Band - Salah Bonge"
                },
                {
                    id: "UCXTzhbfY6viUli-7v_V7RoA",
                    name: 'Hawraar Music'
                },
                {
                    id: "UC371IO_78acNOoN2y1XjMYg",
                    name: 'ABDI ASKARI TUBE'
                },
                {
                    id: "UCucSbMQ70w1nNpekMFe6RKQ",
                    name: "Yurub Geenyo Official"
                },
                {
                    id: "UC86UcBB1qofuGk9as8IhpMQ",
                    name: "Waqal Studio"
                },
                {
                    id: "UCBhPZ6lnTfQZn0bvARil-Xg",
                    name: "Sharma Boy"
                },
                {
                    id: "UCFK3HgpT_Ifb7F8fOcWh2qA",
                    name: "Tusmo Films"
                },
                {
                    id: "UC4aCTV9PWmC9_89kxpzAgpA",
                    name: "Liibaan Jama Studio"
                },
                {
                    id: "UCJ__mZ6PT-0Ikj_LxpaKvtg",
                    name: "Big Moha"
                },
                {
                    id: "UCAON8efxKNHWey6UsFfSGtA",
                    name: "Doni B"
                },
                {
                    id: "UCxCDYZ_-DenztEWJ8UXzyJw",
                    name: "Gantaal Music"
                },
                {
                    id: "UCGC5B6_0C64CwilEb5pjBaw",
                    name: "Haataf Abdi"
                },
                {
                    id: "UC6jptXRFeEcumaH5BPvE-HA",
                    name: "Halimo Gobaad Official"
                },
                {
                    id: "UCmnYJDjq6XU4tVIvdgpv33Q",
                    name: "Hillaac Music"
                },
                {
                    id: "UCppbbj2vr429AeoSAQOq6hw",
                    name: "Hodan & Mursal Channel"
                },
                {
                    id: "UCFPaq219XXXpJKB7MnFe4gg",
                    name: "ISMA IP"
                },
                {
                    id: "UCrqvFSSLLQ0R98kUI6FTGEQ",
                    name: "Jay King Official"
                },
                {
                    id: "UCYKUq58zPgRATf7WElMRQZA",
                    name: "Kiin Jamac"
                },
                {
                    id: "UCdDg_Cj9Bz_tDB0m0Y0Zw5Q",
                    name: "LEEWAAYE STUDIO"
                },
                {
                    id: "UCIhMJeNWmEpcOP_FvqSh6iQ",
                    name: "Naji Productions"
                },
                {
                    id: "UCXxsHtPMKFFsSBuCC9ctaYA",
                    name: "Najma Nashaad Official"
                },
                {
                    id: "UCoV1HJor9gl-XK9K5bVgJjA",
                    name: "Princess Istaahil"
                },
                {
                    id: "UCUvDDOhYK3dxcQg2oYIiMNQ",
                    name: "Sabriina Musse"
                },
                {
                    id: "",
                    name: ""
                }
            ]
        }
    },
    components: {
        Selector
    },
    watch: {
        'form.youtube'(id) {
            this.existingSongs = null
            this._checkExisting(id)
        },
    },
    computed: {
        hasArtist() {
            return Array.isArray(this.form.artist) && this.form.artist.length > 0
        },
        _canSave() {
            return this.hasArtist && !!this.form.name && !this.saving
        },
    },
    methods: {
        ...mapActions("notification", ["showNotification"]),
        _checkExisting: debounce(function (id) {
            if (!id || id.length < 5) return

            axios.get('/api/backstage/songs-by-youtube/' + encodeURIComponent(id))
                .then(response => {
                    // They may have changed the ID while this was loading
                    if (id === this.form.youtube) this.existingSongs = response.data
                })
                .catch(() => {})
        }, 300),
        _youtubeUrl(id) {
            return 'https://www.youtube.com/watch?v=' + encodeURIComponent(id)
        },
        _getContent() {

            let params = { params: { channelId: this.channelId } };
            if (this.page) {
                params.params.pageToken = this.page
            }
            axios.get('/api/backstage/import-youtube', params)
                .then(response => {
                    this.videos = response.data
                })
        },
        _initVideo(video) {
            this.video = video
            this.error = null
            this.form.name = video.snippet.title.toLowerCase()
            this.form.youtube = video.id.videoId
        },
        _dismissEdit() {
            this.video = null;
            this.form = {
                name: null,
                artist: null,
                original_artist: null,
                features: null,
                playlists: null,
                released: null,
                youtube: null,
                cover: null,
                meta: {
                    thumbnail: null,
                    is_youtube: true,
                    cover: false,
                    retry: false,
                    is_smart: false
                },
            }
        },
        _updateCover(cover) {
            this.form.cover = cover;
        },
        _clearCover() {
            this.form.cover = null;
        },
        _saveImport() {
            if (!this._canSave) return

            this.saving = true
            this.error = null
            axios.post('/api/backstage/song-import-youtube', this.form)
                .then(() => {
                    this.showNotification({
                        message: 'Song added',
                        type: 'info',
                    })
                    this._dismissEdit()
                })
                .catch(error => {
                    const errors = error.response?.data?.errors
                    const status = error.response?.status
                    this.error = errors
                        ? Object.values(errors)[0][0]
                        : `Could not save the song${status ? ` (error ${status})` : ''}. Please try again.`
                })
                .finally(() => {
                    this.saving = false
                })
        },
        _next() {
            this.page = this.videos.nextPageToken
            console.log(this.page)
            this._getContent()
        },
        _prev() {
            this.page = this.videos.prevPageToken
            this._getContent()
        }

    }
}
</script>